import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { useSiteContent } from "../../context/ContentContext";
import { IMAGE_SLOTS } from "../../lib/contentSchema";
import ImagePicker from "./ImagePicker";

export default function ImageLibraryEditor() {
  const { content, save } = useSiteContent();
  const [images, setImages] = useState(content?.images || {});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setImages(content?.images || {});
    setDirty(false);
  }, [content?.images]);

  const setImage = (key, url) => {
    setImages((prev) => ({ ...prev, [key]: url }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await save({ images });
      setDirty(false);
      toast.success("Image library saved.");
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-[#E5E0D8] p-7" data-testid="image-library-editor">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6 pb-5 border-b border-[#E5E0D8]">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#1A1A1A]">Image Library</h2>
          <p className="text-sm text-[#6B6B6B] mt-1 max-w-xl">
            Manage every image used across the site. Upload a file or paste a URL.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {dirty && (
            <span className="text-xs uppercase tracking-wider text-[#B8860B] font-semibold">
              Unsaved changes
            </span>
          )}
          <button onClick={handleSave} disabled={saving || !dirty} className="btn-primary disabled:opacity-50" data-testid="save-images">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save All Images"}
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {IMAGE_SLOTS.map((slot) => (
          <div key={slot.key} data-testid={`image-slot-${slot.key}`}>
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#B8860B] mb-2">{slot.label}</div>
            <ImagePicker
              value={images[slot.key]}
              onChange={(url) => setImage(slot.key, url)}
              aspect={slot.aspect}
            />
            <div className="text-[10px] text-[#6B6B6B] mt-1 font-mono-tab">key: {slot.key}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
